// DÍAS CON PREMIO EXTRA (24-oct-2026, pedido Hogar China City: «un día especial y que ese día los puntos sean x1.5»).
// Tarjeta de Mi Negocio → Cómo premio. Reglas por días de la semana o por fechas con un multiplicador (x1.5, x2…);
// las de fecha se quitan solas al pasar (lo hace el backend al leer los ajustes). Se guarda con el botón Guardar del
// formulario (updateBusinessInfo → PUT /loyalty/settings special_days). Función PRO. Backend: lib/specialDays.js.
import React, { useState } from 'react';
import NumInput from '../../../components/ui/NumInput';
import { Link as RouterLink } from 'react-router-dom';
import { CalendarDays, Crown, Plus, Trash2, Sparkles, Info } from 'lucide-react';
import type { SpecialDayRule } from '../../../types/user';
import MultipliersNoStackToggle from './MultipliersNoStackToggle';

const DIAS_CORTO = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];
const DIAS = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const FACTORES = [1.5, 2, 3];
const ORDEN_SEMANA = [1, 2, 3, 4, 5, 6, 0];   // lunes primero, como lo lee la gente

const fmtFecha = (ymd: string) => { const [, m, d] = ymd.split('-'); return `${Number(d)} de ${MESES[Number(m) - 1] || m}`; };
export function describeSpecialDay(r: SpecialDayRule): string {
  if (r.label) return r.label;
  if (r.kind === 'weekday') {
    const ds = [...new Set(r.weekdays || [])].sort().map((d) => DIAS[d]).filter(Boolean);
    if (ds.length === 7) return 'Todos los días';
    const s = ds.length > 1 ? `${ds.slice(0, -1).join(', ')} y ${ds[ds.length - 1]}` : (ds[0] || '');
    return s ? s[0].toUpperCase() + s.slice(1) : '';
  }
  if (r.from === r.to) return `El ${fmtFecha(r.from || '')}`;
  return `Del ${fmtFecha(r.from || '')} al ${fmtFecha(r.to || '')}`;
}
const hoyYmd = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const r2 = (n: number) => Math.round(n * 100) / 100;

interface Props {
  rules: SpecialDayRule[];
  onChange: (rules: SpecialDayRule[]) => void;
  loyaltyMode: 'visits' | 'cashback';
  cashbackPercentage: number;
  pointsPerVisit: number;
  hasPro: boolean;
}

const SpecialDaysCard: React.FC<Props> = ({ rules, onChange, loyaltyMode, cashbackPercentage, pointsPerVisit, hasPro }) => {
  const [abierto, setAbierto] = useState(false);
  const [kind, setKind] = useState<'weekday' | 'date'>('weekday');
  const [weekdays, setWeekdays] = useState<number[]>([]);
  const [from, setFrom] = useState(hoyYmd());
  const [to, setTo] = useState(hoyYmd());
  const [factor, setFactor] = useState<number>(1.5);
  const [custom, setCustom] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const cashback = loyaltyMode === 'cashback';
  const equivale = (f: number) => cashback
    ? `${r2(cashbackPercentage * f)} % de cashback en vez de ${r2(cashbackPercentage)} %`
    : `${r2(pointsPerVisit * f)} puntos por visita en vez de ${r2(pointsPerVisit)}`;

  const agregar = () => {
    setError(null);
    const f = r2(Number(factor));
    if (!Number.isFinite(f) || f < 1.1 || f > 5) return setError('El multiplicador debe estar entre x1.1 y x5.');
    if (kind === 'weekday' && !weekdays.length) return setError('Elige al menos un día de la semana.');
    if (kind === 'date') {
      if (!from || !to) return setError('Elige la fecha de inicio y la de fin.');
      if (to < from) return setError('La fecha de fin no puede ser antes que la de inicio.');
      if (to < hoyYmd()) return setError('Esas fechas ya pasaron.');
    }
    const nueva: SpecialDayRule = kind === 'weekday'
      ? { id: `d${Date.now().toString(36)}`, kind, weekdays: [...weekdays].sort(), factor: f, ...(label.trim() ? { label: label.trim().slice(0, 40) } : {}) }
      : { id: `d${Date.now().toString(36)}`, kind, from, to, factor: f, ...(label.trim() ? { label: label.trim().slice(0, 40) } : {}) };
    onChange([...rules, nueva]);
    setWeekdays([]); setLabel(''); setFactor(1.5); setCustom(false); setAbierto(false);
  };

  return (
    <div className="bg-surface p-5 rounded-lg border border-line shadow-sm" data-testid="dias-premio-extra">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center">
          <CalendarDays className="h-5 w-5 text-emerald-600 mr-2" />
          <h3 className="text-lg font-medium text-content">Días con premio extra</h3>
          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-500/20 text-purple-800 dark:text-purple-300 border border-purple-200 dark:border-purple-500/40">
            <Crown className="w-3 h-3 mr-1 text-purple-500" /> PRO
          </span>
        </div>
      </div>
      <p className="text-sm text-content-3 mb-4">
        Un día de la semana o unas fechas en las que tus clientes ganan más de lo normal (x1.5, x2…). Aplica en el kiosco, el QR y el panel. Las fechas se quitan solas cuando pasan.
      </p>

      {!hasPro ? (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-content-2">Ejemplo: «Martes de puntos x1.5» o «Del 1 al 15 de diciembre, cashback doble». Disponible en el plan PRO.</p>
          <RouterLink to="/dashboard/subscription" className="flex-shrink-0 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-md text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-md">
            <Crown className="h-4 w-4 mr-2" /> Conocer más
          </RouterLink>
        </div>
      ) : (
        <div className="space-y-3">
          {rules.length === 0 && !abierto && (
            <p className="text-sm text-content-3" data-testid="dias-vacio">Todavía no tienes días con premio extra.</p>
          )}
          {rules.length > 0 && (
            <ul className="divide-y divide-line rounded-lg border border-line" data-testid="dias-lista">
              {rules.map((r) => (
                <li key={r.id} className="flex items-center gap-3 px-3 py-2.5" data-testid="dias-regla">
                  <Sparkles className="h-4 w-4 text-amber-500 flex-shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-medium text-content">{describeSpecialDay(r)} <span className="text-emerald-600 font-bold">x{r.factor}</span></span>
                    <span className="block text-xs text-content-3">
                      {equivale(r.factor)}
                      {r.kind === 'date' && r.to && <> · se quita solo después del {fmtFecha(r.to)}</>}
                    </span>
                  </span>
                  <button type="button" onClick={() => onChange(rules.filter((x) => x.id !== r.id))} className="p-1.5 rounded-md text-content-3 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10" title="Quitar" aria-label={`Quitar ${describeSpecialDay(r)}`} data-testid="dias-quitar">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!abierto ? (
            <button type="button" onClick={() => setAbierto(true)} disabled={rules.length >= 10} data-testid="dias-agregar"
              className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-md border border-line text-content-2 hover:bg-surface-2 disabled:opacity-50">
              <Plus className="h-4 w-4 mr-1.5" /> Agregar día con premio extra
            </button>
          ) : (
            <div className="rounded-lg border border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/40 dark:bg-emerald-500/5 p-4 space-y-4 animate-fade-in-up" data-testid="dias-form">
              <div className="flex flex-wrap gap-2">
                {(['weekday', 'date'] as const).map((k) => (
                  <button key={k} type="button" onClick={() => setKind(k)} data-testid={`dias-tipo-${k}`}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${kind === k ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500' : 'border-line bg-surface text-content-2'}`}>
                    {k === 'weekday' ? 'Días de la semana' : 'Fechas'}
                  </button>
                ))}
              </div>

              {kind === 'weekday' ? (
                <div>
                  <label className="block text-sm font-medium text-content-2 mb-2">¿Qué días?</label>
                  <div className="flex flex-wrap gap-2">
                    {ORDEN_SEMANA.map((d) => {
                      const on = weekdays.includes(d);
                      return (
                        <button key={d} type="button" onClick={() => setWeekdays(on ? weekdays.filter((x) => x !== d) : [...weekdays, d])} title={DIAS[d]} aria-pressed={on} data-testid={`dias-dia-${d}`}
                          className={`h-10 w-10 rounded-full text-sm font-semibold border ${on ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                          {DIAS_CORTO[d]}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-content-2 mb-1">Desde</label>
                    <input type="date" value={from} min={hoyYmd()} onChange={(e) => { setFrom(e.target.value); if (to < e.target.value) setTo(e.target.value); }} data-testid="dias-desde"
                      className="block w-full rounded-md border-line shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm bg-surface text-content" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-content-2 mb-1">Hasta</label>
                    <input type="date" value={to} min={from || hoyYmd()} onChange={(e) => setTo(e.target.value)} data-testid="dias-hasta"
                      className="block w-full rounded-md border-line shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm bg-surface text-content" />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-content-2 mb-2">¿Cuánto más ganan?</label>
                <div className="flex flex-wrap gap-2 items-center">
                  {FACTORES.map((f) => (
                    <button key={f} type="button" onClick={() => { setFactor(f); setCustom(false); }} data-testid={`dias-factor-${f}`}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${!custom && factor === f ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500' : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                      x{f}
                    </button>
                  ))}
                  <button type="button" onClick={() => setCustom(true)}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg border ${custom ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-1 ring-emerald-500' : 'border-line bg-surface text-content-2 hover:bg-surface-2'}`}>
                    Otro
                  </button>
                  {custom && (
                    <NumInput min={1.1} max={5} step={0.1} value={factor} onValue={setFactor} data-testid="dias-factor-otro"
                      className="w-24 rounded-md border-line shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm bg-surface text-content" />
                  )}
                </div>
                <p className="mt-1 text-xs text-content-3">Ese día: {equivale(Number(factor) || 1)}.</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-content-2 mb-1">Nombre (opcional)</label>
                <input type="text" value={label} maxLength={40} onChange={(e) => setLabel(e.target.value)} placeholder={kind === 'weekday' ? 'Martes de puntos' : 'Buen Fin'} data-testid="dias-nombre"
                  className="block w-full max-w-xs rounded-md border-line shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm bg-surface text-content" />
              </div>

              {error && <p className="text-sm text-red-600" data-testid="dias-error">{error}</p>}
              <div className="flex gap-2">
                <button type="button" onClick={agregar} data-testid="dias-confirmar" className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-emerald-600 hover:bg-emerald-700 shadow-sm">
                  <Plus className="h-4 w-4 mr-1.5" /> Agregar
                </button>
                <button type="button" onClick={() => { setAbierto(false); setError(null); }} className="px-4 py-2 text-sm font-medium rounded-md border border-line text-content-2 hover:bg-surface-2">Cancelar</button>
              </div>
            </div>
          )}

          <MultipliersNoStackToggle />

          <p className="text-xs text-content-3 flex items-start">
            <Info className="h-3.5 w-3.5 mr-1.5 mt-0.5 flex-shrink-0" />
            <span>Si coinciden varios, se aplica el mayor; con una membresía que ya multiplica no se suman. Los cambios se aplican al pulsar <strong>Guardar</strong>.</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default SpecialDaysCard;
